import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ApiException,
    ScenarioRunClient,
    ScenarioRunList,
    ScenarioSuiteSummary,
} from '../api/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    Breadcrumbs,
    environmentCrumbs,
} from '../components/Breadcrumbs/Breadcrumbs';
import { selectableIds } from '../components/ScenarioRun/ScenarioPicker';
import {
    ISuiteNotice,
    SuiteCard,
    SuiteNotice,
} from '../components/ScenarioRun/SuiteCard';
import { ScenarioCleanup } from '../components/ScenarioRun/ScenarioCleanup';
import { ScenarioRunTable } from '../components/ScenarioRun/ScenarioRunTable';
import { saveScenarioSuite, suiteFailureMessage } from '../api/scenarioSuites';
import { useAxiosSwagger } from '../hooks/useAxiosSwagger';
import { loadingStateType } from './dashboard';
import { useBusy } from '../hooks/useBusy';
import { scenarioRunPath } from '../utils/paths';
import { StartedRunNavigation } from './scenarioRunDetail';

// A run in progress changes its state document every few seconds, so the list refreshes while one is going and
// leaves the API alone once every run has finished.
const REFRESH_WHILE_RUNNING_MS = 5000;

const bySuiteId = (a: ScenarioSuiteSummary, b: ScenarioSuiteSummary) =>
    (a.id ?? '').localeCompare(b.id ?? '');

export const ScenarioRuns = () => {
    const { customerID, environment } = useParams();

    const [loadingState, setLoadingState] = useState<loadingStateType>('init');

    useBusy(loadingState === 'loading', 'Loading the scenario runs');
    const [data, setData] = useState<ScenarioRunList | undefined>(undefined);
    const [suites, setSuites] = useState<ScenarioSuiteSummary[] | undefined>(
        undefined
    );
    const [suitesError, setSuitesError] = useState<string>('');
    const [notice, setNotice] = useState<ISuiteNotice | undefined>(undefined);
    const [uploading, setUploading] = useState(false);
    const uploadInput = useRef<HTMLInputElement>(null);

    const axios = useAxiosSwagger();
    const navigate = useNavigate();

    const load = useCallback(async () => {
        if (!axios) {
            return;
        }

        try {
            const client = new ScenarioRunClient(undefined, axios);
            const response = await client.scenarioRuns(
                customerID as string,
                environment as string
            );

            setData(response);
            setLoadingState('loaded');
        } catch (error) {
            console.log(error);
            // A blip while polling must not throw away the runs that are already on screen.
            setLoadingState((current) =>
                current === 'loaded' ? current : 'error'
            );
        }
    }, [axios, customerID, environment]);

    useEffect(() => {
        setLoadingState('loading');
        load();
    }, [load]);

    // The suites are read once: they change when somebody uploads one, and an upload from this page updates the
    // list itself. A suite that cannot be read is not fatal - it arrives with the reason in `problem`, and its card
    // shows that next to the buttons to fix it.
    useEffect(() => {
        if (!axios) {
            return;
        }

        new ScenarioRunClient(undefined, axios)
            .scenarioSuites(customerID as string, environment as string)
            .then((response) => setSuites([...(response ?? [])].sort(bySuiteId)))
            .catch((error) => {
                console.log(error);
                setSuites([]);
                setSuitesError(
                    ApiException.isApiException(error) && error.response
                        ? error.response
                        : 'The suites could not be read. Check the browser console for details.'
                );
            });
    }, [axios, customerID, environment]);

    // A run that stopped writing its state is not coming back, so polling for it would refresh forever. It is
    // still shown, marked as abandoned rather than as running.
    const hasRunningRun = useMemo(
        () =>
            (data?.runs ?? []).some((run) => !run.finishedAtUtc && !run.stale),
        [data]
    );

    useEffect(() => {
        if (!hasRunningRun) {
            return;
        }

        const timer = setInterval(load, REFRESH_WHILE_RUNNING_MS);

        return () => clearInterval(timer);
    }, [hasRunningRun, load]);

    // A run that was just started is followed on its own page, which waits for its first state by itself.
    const handleStarted = (runId: string) => {
        const started: StartedRunNavigation = { startedAt: Date.now() };

        navigate(scenarioRunPath(customerID, environment, runId), { state: started });
    };

    const handleUpload = async (file?: File) => {
        if (!axios || !file) {
            return;
        }

        setUploading(true);
        setNotice(undefined);

        try {
            const saved = await saveScenarioSuite(
                axios,
                customerID as string,
                environment as string,
                await file.text()
            );

            setSuites((current) =>
                [
                    ...(current ?? []).filter((suite) => suite.id !== saved.id),
                    saved,
                ].sort(bySuiteId)
            );
            setNotice({
                kind: 'success',
                text: `Uploaded ${file.name} as suite ${saved.id}: ${
                    selectableIds(saved).length
                } scenario(s) across ${
                    (saved.profiles ?? []).length
                } profile(s).`,
            });
        } catch (error) {
            console.log(error);
            setNotice({
                kind: 'error',
                text: suiteFailureMessage(
                    error,
                    'The suite could not be uploaded. Check the browser console for details.'
                ),
            });
        }

        setUploading(false);
    };

    const canStart = !!data?.canStart;
    const canManage = !!data?.canManageSuites;
    const busy = hasRunningRun;
    const runs = data?.runs ?? [];
    const lastRunOf = (name?: string) =>
        runs.find((run) => run.suiteName === name);

    return (
        <main className="dashboard-detail scenario-tests">
            <header className="dashboard-detail__header">
                <Breadcrumbs
                    items={[
                        ...environmentCrumbs(customerID, environment),
                        { label: 'Scenario tests' },
                    ]}
                />
                <h2 className="dashboard-detail__title">Scenario tests</h2>
                <p className="scenario-tests__lead">
                    Tick what to run and start. Earlier runs are listed below,
                    newest first.
                </p>
            </header>

            {data && !canManage && data.startBlockedReason && (
                <div className="scenario-tests__notice" role="status">
                    <FontAwesomeIcon icon="ban" />
                    <span>{data.startBlockedReason}</span>
                </div>
            )}

            <h3 className="scenario-tests__group">
                Suites
                {suites && suites.length > 0 && (
                    <span className="scenario-tests__count">
                        {suites.length}
                    </span>
                )}
                <span className="scenario-tests__group-tools">
                    <button
                        type="button"
                        className="arl-suite-tools__btn arl-suite-tools__btn--primary"
                        disabled={!canManage || uploading}
                        title={
                            canManage
                                ? 'A suite file is stored under the "name" it carries. A name that is already taken is refused; use Replace on that suite instead.'
                                : data?.startBlockedReason ?? ''
                        }
                        onClick={() => uploadInput.current?.click()}
                    >
                        <FontAwesomeIcon icon="file-arrow-up" />
                        {uploading ? 'Uploading...' : 'Upload a suite'}
                    </button>
                    <input
                        ref={uploadInput}
                        type="file"
                        accept=".json,application/json"
                        hidden
                        onChange={(event) => {
                            handleUpload(event.target.files?.[0]);
                            // Cleared, so choosing the same file again after fixing it still fires a change.
                            event.target.value = '';
                        }}
                    />
                </span>
            </h3>

            {notice && (
                <SuiteNotice
                    notice={notice}
                    onClose={() => setNotice(undefined)}
                />
            )}

            {!suites && (
                <p className="scenario-tests__muted">
                    <FontAwesomeIcon icon="spinner" spin /> Loading the
                    suites...
                </p>
            )}

            {suitesError && (
                <SuiteNotice notice={{ kind: 'error', text: suitesError }} />
            )}

            {suites && suites.length === 0 && !suitesError && (
                <div className="arl-suite-empty">
                    <FontAwesomeIcon
                        icon="flask"
                        className="arl-suite-empty__icon"
                    />
                    <div>
                        <strong>No suite on this environment yet.</strong>
                        <p>
                            Upload a suite file with the button above, or from
                            the command line:
                        </p>
                        <code>
                            dotnet run --project
                            tools/PossibilIT.Connector.Tools.ScenarioRunner --
                            upload-suite --suite-file
                            build/scenario-tests/ticket-1677-revenue-periods-suite.json
                            --customer-code {customerID} --environment{' '}
                            {environment}
                        </code>
                    </div>
                </div>
            )}

            {data &&
                (suites ?? []).map((suite) => (
                    <SuiteCard
                        key={suite.id}
                        customerID={customerID as string}
                        environment={environment as string}
                        suite={suite}
                        lastRun={lastRunOf(suite.name)}
                        defaultOpen={(suites ?? []).length === 1}
                        canStart={canStart}
                        canManage={canManage}
                        startBlockedReason={data.startBlockedReason}
                        busy={busy}
                        onStarted={handleStarted}
                        onReplaced={(saved) =>
                            setSuites((current) =>
                                (current ?? []).map((candidate) =>
                                    candidate.id === saved.id
                                        ? saved
                                        : candidate
                                )
                            )
                        }
                        onDeleted={(id) => {
                            setSuites((current) =>
                                (current ?? []).filter(
                                    (candidate) => candidate.id !== id
                                )
                            );
                            setNotice({
                                kind: 'success',
                                text: `Deleted suite ${id}. Its runs stay in the history below.`,
                            });
                        }}
                    />
                ))}

            <h3 className="scenario-tests__group">
                Run history
                {runs.length > 0 && (
                    <span className="scenario-tests__count">{runs.length}</span>
                )}
                {hasRunningRun && (
                    <span className="scenario-tests__muted">
                        <FontAwesomeIcon icon="spinner" spin /> a run is going
                    </span>
                )}
            </h3>

            {data && canManage && runs.length > 0 && (
                <div className="arl-cleanup__bar">
                    <ScenarioCleanup
                        customer={customerID as string}
                        environment={environment as string}
                        disabled={hasRunningRun}
                        disabledReason="Wait until the run that is going has finished."
                    />
                </div>
            )}

            <ScenarioRunTable
                customerID={customerID}
                environment={environment}
                runs={runs}
                onStopped={load}
                loadingState={loadingState}
            />
        </main>
    );
};
